import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { isCompanyUidFormatInvalid } from './companyUid';

const UID_DIGITS = 9;

export type CompanyUidFieldProps = {
    label: string;
    value: string;
    // Omitted where the UID is only displayed — the validation is the one place it is entered.
    onChange?: (next: string) => void;
    disabled?: boolean;
};

export function CompanyUidField({ label, value, onChange, disabled = false }: CompanyUidFieldProps): ReactElement {
    const { t } = useTranslation();
    const formatInvalid = isCompanyUidFormatInvalid(value);

    const handleChange = (next: string): void => {
        const sanitized = next.replace(/[^\d.]/g, '');
        if (sanitized.replace(/\D/g, '').length > UID_DIGITS) {
            return;
        }
        onChange?.(sanitized);
    };

    return (
        <TextField
            label={label}
            placeholder="123.456.789"
            fullWidth
            value={value}
            disabled={disabled}
            onChange={(e) => handleChange(e.target.value)}
            error={formatInvalid}
            helperText={
                formatInvalid ? (
                    t('SETTINGS.VERIFICATION.UID_FORMAT_ERROR')
                ) : (
                    <Trans
                        i18nKey="SETTINGS.VERIFICATION.UID_HELPER"
                        components={{
                            uidLink: (
                                <Link
                                    href={t('SETTINGS.VERIFICATION.UID_REGISTER_URL')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="always"
                                />
                            ),
                        }}
                    />
                )
            }
            slotProps={{
                formHelperText: { component: 'div' },
                input: {
                    startAdornment: <InputAdornment position="start">CHE-</InputAdornment>,
                },
            }}
        />
    );
}
